export type AttentionReason = 'input-needed' | 'new-reply'
export type AttentionSource =
  | 'chat' | 'workflow-copilot' | 'skill-copilot' | 'soul-copilot'
  | 'authoring-checkpoint' | 'in-flight-scoping'

export interface AttentionEvent {
  /** Stable identity; the same event always produces the same id. */
  id: string
  source: AttentionSource
  reason: AttentionReason
}

export const ATTENTION_LABEL: Record<AttentionReason, string> = {
  'input-needed': 'Input needed',
  'new-reply': 'New reply',
}

export function attentionTitle(base: string, reason: AttentionReason): string {
  return `${ATTENTION_LABEL[reason]} · ${base}`
}

export function topReason(pending: readonly AttentionEvent[]): AttentionReason | null {
  if (pending.some(event => event.reason === 'input-needed')) return 'input-needed'
  return pending.length ? 'new-reply' : null
}

export function isTabActive(doc: Document = document): boolean {
  return doc.visibilityState === 'visible' && doc.hasFocus()
}

let localId = 0
export function localAttentionId(prefix: string): string {
  localId += 1
  return `${prefix}:local-${localId}`
}

export function copilotJobAttentionId(response: { job_id?: string }, fallbackPrefix: string): string {
  return response.job_id ? `copilot-job:${response.job_id}` : localAttentionId(fallbackPrefix)
}

export class AttentionStore {
  private pending: readonly AttentionEvent[] = []
  private readonly seen = new Set<string>()
  private readonly listeners = new Set<() => void>()

  getPending = (): readonly AttentionEvent[] => this.pending

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => { this.listeners.delete(listener) }
  }

  notify(event: AttentionEvent, tabActive: boolean): boolean {
    if (this.seen.has(event.id)) return false
    this.seen.add(event.id)
    if (tabActive) return false
    this.pending = [...this.pending, event]
    this.emit()
    return true
  }

  acknowledge(): void {
    if (this.pending.length === 0) return
    this.pending = []
    this.emit()
  }

  hasSeen(id: string): boolean { return this.seen.has(id) }

  private emit(): void {
    for (const listener of this.listeners) listener()
  }
}
